import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { selectUserContext } from 'core/userContext';
import { useMock } from 'api/DashboardAPI';
import { fetchMetricsRequest } from 'modules/metrics';
import Dashboard from './Dashboard';
import {
  addWidget,
  closeAddEditModal,
  closeDeleteModal,
  closeSettingsModal,
  changeDefaultEndRange,
  changeDefaultStartRange,
  changeEndDate,
  changeEndTime,
  changeStartDate,
  changeStartTime,
  changeTheme,
  deleteWidget,
  fetchCampaigns,
  fetchDashboard,
  fetchDashboardData,
  layoutChange,
  openAddEditModal,
  openDeleteModal,
  openSettingsModal,
  saveDashboard,
  saveWidget,
  setDashboard,
  resetDashboard,
} from '../actions';
import {
  selectAddEditModalOpen,
  selectAddMode,
  selectDashboardData,
  selectDefaultEndRange,
  selectDefaultStartRange,
  selectDeleteModalOpen,
  selectEndDate,
  selectEndTime,
  selectFetchingDashboard,
  selectFetchingData,
  selectHighLevelMetrics,
  selectLayout,
  selectLayoutLoading,
  selectSelectedWidget,
  selectSettingsModalOpen,
  selectStartDate,
  selectStartTime,
  selectTheme,
  selectWidgets,
} from '../selectors';

export class DashboardContainer extends React.Component {
  /**
   * componentDidMount
   * Fetches dashboard layout data when dashboard component mounts.
   * @return { void }
   */
  componentDidMount() {
    this.props.fetchDashboard();
    this.props.fetchMetricsRequest();
  }

  /**
   * componentWillUnmount
   * Resets dashboard state on unmount.
   * @return { void }
   */
  componentWillUnmount = () => {
    this.props.resetDashboard();
  };

  /**
   * shouldComponentUpdate
   * Fetches data after dashboard layout is fetched.
   * @param { Object } nextProps: Updated props for the dashboard.
   * @returns { Boolean } If dashboard should update.
   */
  shouldComponentUpdate = (nextProps) => {
    const { dashboardData, widgets, fetchingData } = nextProps;
    if (widgets && !fetchingData && dashboardData && !Object.keys(dashboardData).length) {
      this.fetchAllDashboardData();
    }
    return true;
  };

  /**
   * fetchAllDashboardData
   * Fetches data for all widgets.
   * @param { Object } newWidgets: Fetch data on new widgets. (Forces fetch when passed in.)
   * @return { void }
   */
  fetchAllDashboardData = (newWidgets) => {
    const { widgets, fetchingData, fetchDashboardData: fetchData, startDate, endDate } = this.props;
    if (newWidgets || (!fetchingData && widgets && Object.keys(widgets).length)) {
      fetchData(newWidgets || widgets, startDate, endDate);
    }
  };

  render() {
    return <Dashboard fetchDashboardData={this.fetchAllDashboardData} {...this.props} />;
  }
}

const mapStateToProps = (state) => ({
  addEditModalOpen: selectAddEditModalOpen(state),
  addMode: selectAddMode(state),
  dashboardData: selectDashboardData(state),
  defaultEndRange: selectDefaultEndRange(state),
  defaultStartRange: selectDefaultStartRange(state),
  deleteModalOpen: selectDeleteModalOpen(state),
  endDate: selectEndDate(state),
  endTime: selectEndTime(state),
  fetchingDashboard: selectFetchingDashboard(state),
  fetchingData: selectFetchingData(state),
  highLevelMetrics: selectHighLevelMetrics(state),
  isMockMode: useMock(),
  layout: selectLayout(state),
  layoutLoading: selectLayoutLoading(state),
  selectedWidget: selectSelectedWidget(state),
  settingsModalOpen: selectSettingsModalOpen(state),
  startDate: selectStartDate(state),
  startTime: selectStartTime(state),
  theme: selectTheme(state),
  userContext: selectUserContext(state),
  widgets: selectWidgets(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      addWidget,
      changeDefaultEndRange,
      changeDefaultStartRange,
      changeEndDate,
      changeEndTime,
      changeStartDate,
      changeStartTime,
      changeTheme,
      closeAddEditModal,
      closeDeleteModal,
      closeSettingsModal,
      deleteWidget,
      fetchCampaigns,
      fetchDashboard,
      fetchDashboardData,
      fetchMetricsRequest,
      layoutChange,
      openAddEditModal,
      openDeleteModal,
      openSettingsModal,
      resetDashboard,
      saveDashboard,
      saveWidget,
      setDashboard,
    },
    dispatch,
  );

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DashboardContainer));
